/*global google*/

import Config from '../config/config';

const { googleMapsApiKey } = Config;

// Digs through the autcomplete object to find formattedPrediction
// Needed because the dang thing changes field names seemingly at random
const findFormattedPrediction = (obj, maxDepth = 4) => {
  if (!maxDepth) {
    return null;
  }

  for (const key of Object.keys(obj)) {
    if (key === 'formattedPrediction' && typeof obj[key] === 'string') {
      return obj[key];
    } else if (obj[key] && typeof obj[key] === 'object') {
      const nestedPrediction = findFormattedPrediction(obj[key], maxDepth - 1);
      if (nestedPrediction) {
        return nestedPrediction;
      }
    }
  }
  return null;
};

export default class GoogleHelper {
  static uninstall_autocomplete() {
    const googleAutocompleteNode = document.querySelector('.pac-container');

    if (googleAutocompleteNode) {
      googleAutocompleteNode.parentNode.removeChild(googleAutocompleteNode);
    }
  }
  static install_autocomplete(target, zip, onPlaceChange) {
    this.loadApis(() => {
      const autocomplete = new google.maps.places.Autocomplete(target, {
        componentRestrictions: { country: ['us'] },
        types: ['address'],
        fields: ['address_components'],
      });

      let locationBeingChanged = false;

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const prediction = findFormattedPrediction(autocomplete);
        const { streetAddress, zip, city, state } = this.getAddressComponents(place, prediction);
        locationBeingChanged = false;
        onPlaceChange(streetAddress, zip, city, state);
      });

      google.maps.event.addDomListener(target, 'keydown', (event) => {
        // enter/return (13) or tab (9)
        if (event.keyCode === 13 || event.keyCode === 9) {
          if (locationBeingChanged) {
            event.preventDefault();
            if (event.keyCode === 13) {
              event.stopPropagation();
              target.blur();
            }
          }
        } else {
          locationBeingChanged = true;
        }
      });

      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ address: `${zip}`, componentRestrictions: { country: 'us' } }, (results) => {
        if (!results || results.length === 0) {
          return;
        }
        if (results && results.length > 1) {
          console.warn('Geocode returned more than one result'); // eslint-disable-line no-console
        }
        let bounds = results[0].geometry.bounds;
        if (!bounds) {
          bounds = new google.maps.LatLngBounds(
            results[0].geometry.viewport.getSouthWest(),
            results[0].geometry.viewport.getNorthEast()
          );
        }
        autocomplete.setBounds(bounds);
      });
    });
  }

  static getAddressComponents(place, prediction) {
    let streetAddress = '';
    let zip = '';
    let city = '';
    let state = '';
    const placeReader = {
      street_number: (component) => (streetAddress = `${component.long_name} ${streetAddress}`),
      route: (component) => (streetAddress += component.short_name),
      postal_code: (component) => (zip = component.long_name),
      locality: (component) => (city = component.long_name),
      administrative_area_level_1: (component) => (state = component.short_name),
    };
    for (const component of place.address_components) {
      // console.log('component', component);
      const compKey = component.types.find((t) => Object.keys(placeReader).includes(t));
      if (compKey) {
        placeReader[compKey](component);
      }
    }

    // if there isn't a street_number or locality in the components, then we need to fallback to parsing pieces of the prediction
    if (!place.address_components?.some((component) => component.types?.includes('street_number')) && prediction) {
      streetAddress = prediction.split(',')?.[0] ?? '';
    }
    if (city === '' && prediction) {
      const predictionComponents = prediction.split(',');
      if (predictionComponents.length >= 3) {
        city = predictionComponents[predictionComponents.length - 3]?.trim();
      }
    }
    return { streetAddress, zip, city, state };
  }

  static loadApis(onLoad) {
    require('scriptjs')('https://maps.googleapis.com/maps/api/js?libraries=places&key=' + googleMapsApiKey, onLoad);
  }

  static checkForStreetViewData(clat, clon, callback) {
    const streetViewService = new google.maps.StreetViewService();
    const STREETVIEW_MAX_DISTANCE = 100;
    const latLng = new google.maps.LatLng(clat, clon);

    streetViewService.getPanoramaByLocation(latLng, STREETVIEW_MAX_DISTANCE, function (streetViewPanoramaData, status) {
      if (status === google.maps.StreetViewStatus.OK) {
        callback();
      }
    });
  }

  static async validateAddress({ address = '', city, state, zip }) {
    return await fetch(`https://addressvalidation.googleapis.com/v1:validateAddress?key=${googleMapsApiKey}`, {
      method: 'POST',
      headers: {
        'X-Goog-FieldMask':
          'result.address.addressComponents,result.address.postalAddress,result.address.missingComponentTypes',
      },
      body: JSON.stringify({
        address: { regionCode: 'US', addressLines: [address, [city, state, zip].filter((x) => !!x).join(', ')] },
      }),
    });
  }
}
